import { useCallback } from 'react';

import Colors from '@dynatrace/strato-design-tokens/colors';

import type { RectanglePoint } from './types.js';
import { useCanvas } from '../../../../core/components/canvas/hooks/useCanvas.js';
import { getCanvasColor } from '../../../../core/components/canvas/utils/canvas-color.js';
import { DEFAULT_HIGHLIGHTED_OPACITY } from '../../../../core/constants.js';

interface HighlightedRectangleCanvasProps {
  points: RectanglePoint[];
}

const OUTER_OFFSET = 2;
const OUTER_SHRINK = 3;
const INNER_OFFSET = 3;
const INNER_SHRINK = 5;
const BORDER_WIDTH = 1;

export const HighlightedRectangleCanvas = ({
  points,
}: HighlightedRectangleCanvasProps) => {
  const draw = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      for (const point of points) {
        // Outer border
        ctx.beginPath();
        ctx.lineWidth = BORDER_WIDTH;
        ctx.strokeStyle = getCanvasColor(Colors.Text.Neutral.Default);
        ctx.rect(
          Number(point.x) + OUTER_OFFSET,
          Number(point.y) + OUTER_OFFSET,
          Math.max(0, Number(point.width) - OUTER_SHRINK),
          Math.max(0, Number(point.height) - OUTER_SHRINK),
        );
        ctx.stroke();
        ctx.closePath();

        // Calculate inner rectangle dimensions
        const innerX = Number(point.x) + INNER_OFFSET;
        const innerY = Number(point.y) + INNER_OFFSET;
        const innerWidth = Number(point.width) - INNER_SHRINK;
        const innerHeight = Number(point.height) - INNER_SHRINK;

        // Only draw inner border if it has positive size
        if (innerWidth > 0 && innerHeight > 0) {
          ctx.beginPath();
          ctx.lineWidth = BORDER_WIDTH;
          ctx.globalAlpha = DEFAULT_HIGHLIGHTED_OPACITY;
          ctx.strokeStyle = getCanvasColor(Colors.Background.Base.Default);
          ctx.rect(innerX, innerY, innerWidth, innerHeight);
          ctx.stroke();
          ctx.globalAlpha = 1;
          ctx.closePath();
        }
      }

      return false;
    },
    [points],
  );

  useCanvas('Highlighted-rects', draw);

  return null;
};
